import { useEffect, useMemo, useRef, useState } from 'react';
import { MenuContext } from './MenuContext';
import { PermissionFlags } from '../../features/admin/roles/PermissionFlags';
import useSession from '../../hooks/useSession';
import { Link } from 'react-router';
import { useWindowSize } from 'usehooks-ts';

export interface ButtonData {
    text?: React.ReactNode;
    icon?: React.ReactNode;
    to?: string;
    onClick?: React.MouseEventHandler;
    type?: 'info' | 'danger' | 'divider';
    disabled?: boolean;
    ID?: string;
    requireSession?: boolean;
    permission?: PermissionFlags;
    userID?: number;
}

export interface MenuData {
    x: number;
    y: number;
    buttons: ButtonData[];
}

export default function MenuContextProvider({ children }: { children?: React.ReactNode }) {
    const [menuData, setMenuData] = useState<MenuData>();
    const menuRef = useRef<HTMLDivElement>(null);
    const session = useSession();

    useEffect(() => {
        function close(e: MouseEvent) {
            // Only close if the event target wasn't the context menu
            if ((e.target as HTMLDivElement).offsetParent != menuRef.current) {
                setMenuData(undefined);
            }
        }

        function closeOnScroll() {
            setMenuData(undefined);
        }

        document.addEventListener('click', close);
        document.addEventListener('scroll', closeOnScroll);

        return () => {
            document.removeEventListener('click', close);
            document.removeEventListener('scroll', closeOnScroll);
        };
    }, []);

    function handleClick(e: React.MouseEvent<HTMLButtonElement, MouseEvent>, b: ButtonData) {
        if (b.onClick !== undefined) b.onClick(e);

        setMenuData(undefined);
    }

    const filteredButtons = useMemo(
        () =>
            menuData?.buttons.filter((button) => {
                if (button.requireSession && !session.user) return false;

                if (button.permission && button.userID) {
                    if (!session.hasPermission(button.permission) && session.user?.ID !== button.userID) return false;
                } else if (button.permission && !session.hasPermission(button.permission)) return false;
                else if (button.userID && button.userID !== session.user?.ID) return false;
                return true;
            }),
        [menuData?.buttons, session],
    );

    const windowSize = useWindowSize();

    return (
        <MenuContext.Provider value={{ menuData, setMenuData }}>
            {children}
            {menuData && (
                <div
                    ref={menuRef}
                    className='fixed z-50 bg-theme-900 text-theme-text text-sm w-60 round:rounded-lg shadow-2xl'
                    style={{
                        left: `${Math.max(8, Math.min(menuData.x, windowSize.width - 248))}px`,
                        top: `${menuData.y}px`,
                    }}
                >
                    {
                        <ul className='p-1'>
                            {filteredButtons?.map((b, i) =>
                                b.type === 'divider' ? (
                                    <li key={b.ID ?? i} className='bg-theme-500 h-0.5 my-1' />
                                ) : (
                                    <li key={b.ID}>
                                        {b.to ? (
                                            <Link
                                                to={b.to}
                                                onClick={() => setMenuData(undefined)}
                                                className={
                                                    'flex items-center w-full text-start pe-4 py-1 round:rounded-lg ' +
                                                    (!(b.type === 'danger')
                                                        ? 'hover:bg-theme-700'
                                                        : 'hover:bg-red-600') +
                                                    (b.disabled
                                                        ? ' text-theme-400 line-through pointer-events-none'
                                                        : '')
                                                }
                                            >
                                                <span className='inline-flex items-center justify-center w-4 mx-2'>
                                                    {b.icon}
                                                </span>
                                                {b.text}
                                            </Link>
                                        ) : (
                                            <button
                                                onClick={(e) => handleClick(e, b)}
                                                className={
                                                    'w-full text-start pe-4 py-1 round:rounded-lg flex items-center ' +
                                                    (!(b.type === 'danger')
                                                        ? 'hover:bg-theme-700'
                                                        : 'hover:bg-red-600') +
                                                    (b.disabled
                                                        ? ' text-theme-400 line-through pointer-events-none'
                                                        : '')
                                                }
                                            >
                                                <span className='inline-flex items-center justify-center w-4 mx-2'>
                                                    {b.icon}
                                                </span>
                                                <span>{b.text}</span>
                                            </button>
                                        )}
                                    </li>
                                ),
                            )}
                        </ul>
                    }
                </div>
            )}
        </MenuContext.Provider>
    );
}
